import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { ResultActions } from "@/components/ResultActions";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Swords, Loader2, Dice5, CheckCircle2, Map, Ban, Target, Crown, PenLine } from "lucide-react";

const DIFFICULTIES = ["Surprise Me", "Casual", "Tricky", "Hard", "Nightmare"];

const diffColor = (d) => ({
  Casual: "text-green-400 border-green-400/40 bg-green-400/10",
  Tricky: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10",
  Hard: "text-orange-400 border-orange-400/40 bg-orange-400/10",
  Nightmare: "text-primary border-primary/40 bg-primary/10",
}[d] || "text-foreground/70 border-white/20 bg-white/5");

export default function ChallengeGenerator() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("Killer");
  const [difficulty, setDifficulty] = useState("Surprise Me");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [version, setVersion] = useState(0);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ title: "", role: "Killer", difficulty: "Hard", character: "", perk_restriction: "", map_restriction: "", objective: "", description: "", rules: "" });
  const isPro = user?.is_pro;

  const generate = async () => {
    if (!user) return setAuthOpen(true);
    setLoading(true);
    setChallenge(null);
    try {
      const body = { role };
      if (difficulty !== "Surprise Me") body.difficulty = difficulty;
      const { data } = await api.post("/generate/challenge", body);
      setChallenge(data);
      setVersion((v) => v + 1);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Generation failed, please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveCustom = async () => {
    const data = { ...custom, rules: custom.rules.split("\n").map((r) => r.trim()).filter(Boolean), custom: true };
    try {
      await api.post("/saved", { kind: "challenge", data });
      setChallenge(data);
      setCustomOpen(false);
      toast.success("Custom challenge created & saved!");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Could not save custom challenge.");
      }
    }
  };

  const markComplete = async () => {
    if (!user) return setAuthOpen(true);
    setCompleting(true);
    try {
      await api.post("/profile/complete-challenge");
      await refreshUser();
      toast.success("Challenge logged! Your rank grows.");
    } catch {
      toast.error("Could not log challenge.");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div data-testid="challenge-gen-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Swords className="h-5 w-5" />
        <span className="text-xs font-bold uppercase tracking-[0.25em]">Challenge Generator</span>
      </div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">Forge a <span className="text-primary">trial</span></h1>
      <p className="mt-3 max-w-2xl text-foreground/60">Random self-imposed challenges with perk, map and difficulty restrictions.</p>

      <div className="mt-8"><UpgradeBanner version={version} /></div>

      <div className="grid gap-5 border border-border bg-card p-6 md:p-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Role</p>
          <div className="flex gap-2.5">
            {["Killer", "Survivor"].map((r) => (
              <button key={r} data-testid={`role-${r}`} onClick={() => setRole(r)} className={`flex-1 border px-4 py-3 text-sm font-bold uppercase tracking-widest transition-all md:flex-none md:px-10 ${role === r ? "border-primary bg-primary text-primary-foreground" : "border-white/10 text-foreground/70 hover:border-primary/50"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Difficulty</p>
          <div className="flex flex-wrap gap-2.5">
            {DIFFICULTIES.map((d) => (
              <button key={d} data-testid={`difficulty-${d.replace(/\s/g, "-")}`} onClick={() => setDifficulty(d)} className={`border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${difficulty === d ? "border-primary bg-primary text-primary-foreground" : "border-white/10 text-foreground/70 hover:border-primary/50"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="generate-challenge-btn" onClick={generate} disabled={loading} className="rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 md:px-10">
            {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Forging…</> : <><Dice5 className="mr-2 h-5 w-5" /> Generate Challenge</>}
          </Button>
          <Button
            data-testid="custom-challenge-btn"
            variant="outline"
            onClick={() => isPro ? setCustomOpen(true) : navigate("/pricing")}
            className="rounded-sm border-primary/40 bg-transparent py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/10 md:px-8"
          >
            <PenLine className="mr-2 h-4 w-4" /> Custom Challenge
            <span className="ml-2 border border-primary/40 bg-primary/10 px-2 py-0.5 text-[9px] tracking-widest text-primary">Pro</span>
          </Button>
        </div>
      </div>

      {challenge && (
        <div data-testid="challenge-result" className="fade-up mt-6 border border-primary/30 bg-card p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{challenge.role}</span>
            <span className={`border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${diffColor(challenge.difficulty)}`}>{challenge.difficulty}</span>
            {challenge.character && <span className="border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-foreground/70">{challenge.character}</span>}
          </div>
          <h2 className="mt-4 font-display text-3xl tracking-wide md:text-4xl">{challenge.title}</h2>
          <p className="mt-3 text-foreground/70">{challenge.description}</p>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="border border-white/10 bg-background/50 p-4">
              <div className="flex items-center gap-2 text-primary"><Ban className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">Perks</span></div>
              <p className="mt-2 text-sm text-foreground/70">{challenge.perk_restriction}</p>
            </div>
            <div className="border border-white/10 bg-background/50 p-4">
              <div className="flex items-center gap-2 text-primary"><Map className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">Map</span></div>
              <p className="mt-2 text-sm text-foreground/70">{challenge.map_restriction}</p>
            </div>
            <div className="border border-white/10 bg-background/50 p-4">
              <div className="flex items-center gap-2 text-primary"><Target className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">Objective</span></div>
              <p className="mt-2 text-sm text-foreground/70">{challenge.objective}</p>
            </div>
          </div>

          {Array.isArray(challenge.rules) && (
            <div className="mt-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-foreground/60">Rules</p>
              <ul className="space-y-2">
                {challenge.rules.map((r) => (
                  <li key={r} className="flex gap-3 text-sm text-foreground/70"><span className="mt-1 h-1.5 w-1.5 shrink-0 bg-primary" />{r}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-white/10 pt-5">
            <Button data-testid="complete-challenge-btn" onClick={markComplete} disabled={completing} className="rounded-sm bg-primary uppercase tracking-widest hover:bg-primary/90">
              {completing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Mark Completed
            </Button>
            <ResultActions kind="challenge" data={challenge} onRequireAuth={() => setAuthOpen(true)} />
          </div>
        </div>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-sm border-white/10 bg-card">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-3xl tracking-wider"><Crown className="h-5 w-5 text-primary" /> Custom Challenge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title"><Input data-testid="custom-title" value={custom.title} onChange={(e) => setCustom({ ...custom, title: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role"><Input value={custom.role} onChange={(e) => setCustom({ ...custom, role: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
              <Field label="Difficulty"><Input value={custom.difficulty} onChange={(e) => setCustom({ ...custom, difficulty: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            </div>
            <Field label="Character (optional)"><Input value={custom.character} onChange={(e) => setCustom({ ...custom, character: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Field label="Perk restriction"><Input value={custom.perk_restriction} onChange={(e) => setCustom({ ...custom, perk_restriction: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Field label="Map restriction"><Input value={custom.map_restriction} onChange={(e) => setCustom({ ...custom, map_restriction: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Field label="Objective"><Input value={custom.objective} onChange={(e) => setCustom({ ...custom, objective: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Field label="Description"><Textarea value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Field label="Rules (one per line)"><Textarea value={custom.rules} onChange={(e) => setCustom({ ...custom, rules: e.target.value })} className="rounded-sm border-white/10 bg-background" /></Field>
            <Button data-testid="custom-save-btn" onClick={saveCustom} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest">Create & Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}


const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-widest text-foreground/70">{label}</Label>
    {children}
  </div>
);
